import { createSlice } from '@reduxjs/toolkit';
import {
  login,
  logout,
  refreshAuthToken,
  initializeAuth,
  authenticateWithToken,
  fetchCurrentUser,
} from '../actions/authThunks';

/**
 * This Redux slice manages authentication state and actions.
 * It handles user data, authentication status, initialization, and error states,
 * providing centralized control for authentication-related functionality.
 *
 * Actions Tracked:
 * - login: Tracks the user login process and updates state.
 * - logout: Clears user data, tokens, and resets authentication state.
 * - refreshAuthToken: Refreshes access tokens to maintain session.
 * - initializeAuth: Initializes authentication state based on stored tokens.
 */
const initialState = {
  user: null,
  isAuthenticated: false,
  isAuthInitializing: true,
  error: null,
  /** Step for create-organization (1 or 2), set after login when GET /api/v3/organizations/{id} runs. */
  createOrgStep: null,
  /** Onboarding status from GET /api/v3/onboarding/status, set after login (after getCurrentUser + org info). */
  onboardingData: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetAuthState: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.isAuthInitializing = false;
      state.error = null;
      state.createOrgStep = null;
      state.onboardingData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.isAuthInitializing = false;
        const payload = action.payload;
        state.user = payload?.user ?? payload;
        state.createOrgStep = payload?.createOrgStep ?? null;
        state.onboardingData = payload?.onboardingData ?? null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isAuthenticated = false;
        state.user = null;
        state.createOrgStep = null;
        state.onboardingData = null;
        state.error = action.payload;
      })

      // Logout – storage is cleared in logout thunk (both localStorage and sessionStorage)
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.createOrgStep = null;
        state.onboardingData = null;
        state.isAuthInitializing = false;
        state.error = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Refresh Token
      .addCase(refreshAuthToken.fulfilled, (state) => {
        state.isAuthenticated = true;
      })
      .addCase(refreshAuthToken.rejected, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.error = 'Session expired. Please log in again.';
      })

      // Initialize Auth
      .addCase(initializeAuth.pending, (state) => {
        state.isAuthInitializing = true;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.isAuthenticated = action.payload.isAuthenticated;
        state.isAuthInitializing = false;
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.isAuthInitializing = false;
        state.error = null;
      })
      .addCase(authenticateWithToken.pending, (state) => {
        state.error = null;
      })
      .addCase(authenticateWithToken.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.user = action.payload;
        localStorage.setItem('user', JSON.stringify(action.payload));
      })
      .addCase(authenticateWithToken.rejected, (state, action) => {
        state.isAuthenticated = false;
        state.user = null;
        state.error = action.payload;
      })
      // Fetch current user (e.g. after create organization)
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        const user = action.payload?.data ?? action.payload;
        state.user = user;
        state.isAuthenticated = true;
        if (user) localStorage.setItem('user', JSON.stringify(user));
      })
      .addCase(fetchCurrentUser.rejected, (state) => {
        state.user = null;
      });
  },
});

export const { resetAuthState } = authSlice.actions;
export default authSlice.reducer;
